import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Box, IconButton, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { addDays, addMonths, endOfWeek, format, startOfMonth, startOfWeek, subDays, subMonths } from "date-fns";
import { he } from "date-fns/locale";
import React, { useState } from "react";
import "./DatePicker.scss";

interface CustomDatePickerProps {
  onDateChange: (date: Date | null) => void;
  onViewChange?: (viewType: "dayGridMonth" | "timeGridWeek") => void;
  view: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  onDateChange,
  onViewChange,
  view,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    onDateChange(date);

    if (date && onViewChange) {
      const today = new Date();
      const startOfSelectedWeek = startOfWeek(date);
      const endOfSelectedWeek = endOfWeek(date);

      if (
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      ) {
        onViewChange("dayGridMonth");
      } else if (startOfSelectedWeek <= today && endOfSelectedWeek >= today) {
        onViewChange("timeGridWeek");
      }
    }
  };

  const formatDate = (date: Date | null) => {
    return date ? format(date, "EEEE, dd MMMM yyyy", { locale: he }) : "";
  };

  const handlePrevDay = () => {
    if (selectedDate) {
      let newDate;
      if (view === "dayGridMonth") {
        // Move to the first day of the previous month
        newDate = startOfMonth(subMonths(selectedDate, 1));
      } else {
        // Default behavior for other views
        newDate = subDays(
          selectedDate,
          view === "timeGridWeek" ? 7 : 1
        );
      }
      setSelectedDate(newDate);
      onDateChange(newDate);
    }
  };

  const handleNextDay = () => {
    if (selectedDate) {
      let newDate;
      if (view === "dayGridMonth") {
        // Move to the first day of the next month
        newDate = startOfMonth(addMonths(selectedDate, 1));
      } else {
        // Default behavior for other views
        newDate = addDays(
          selectedDate,
          view === "timeGridWeek" ? 7 : 1
        );
      }
      setSelectedDate(newDate);
      onDateChange(newDate);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          border: "1px solid #e4e4e4",
          borderRadius: "24px",
          width: "36vw",
          display: "flex",
          justifyContent: "space-around",
          height: "7vh",
        }}
      >
        <IconButton onClick={handlePrevDay}>
          <ArrowForward />
        </IconButton>
        <DatePicker
          sx={{
            width: "fit-content",
          }}
          className="custom-date-picker"
          value={selectedDate}
          onChange={handleDateChange}
          slots={{
            textField: (params) => (
              <TextField
                {...params}
                value={formatDate(selectedDate)}
                className="rendered-date-picker"
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "תאריך",
                }}
              />
            ),
          }}
        />
        <IconButton onClick={handleNextDay}>
          <ArrowBack />
        </IconButton>
      </Box>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
